'use client';

import { ReactElement, useEffect, useState } from 'react';
import Experiment, { When, useExperimentBucket } from '@carvana/experiment';
import Cookie from '@carvana/stc-cookie';
import { Environment } from '@carvana/experiment/SharedTypes';
import { getUserState } from '@carvana/core-auth';
import Spinner from '@carvana/ds-spinner';
import { datadogRum } from '@datadog/browser-rum';

const LoadingWrapper = () => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '90vh'
    }}
    role="status"
    aria-label="Loading experiment content"
  >
    <Spinner size="lg" />
  </div>
);
declare global {
  interface Window {
    FS: {
      setUserVars: (vars: { [x: string]: string }) => void;
    };
  }
}

export type LinkedExperiment = {
  experimentName: string;
  identifierType: string;
};

export type AdditionalExperimentProps = {
  token?: string;
  nonBucketedExperiment?: boolean;
  linkedExperiment?: LinkedExperiment;
  linkedIdentifier?: string;
};

type WithExperimentProps = {
  BUCKETS: string[];
  IDENTIFIER: string;
  NAME: string;
  NON_BUCKETED?: boolean;
  BLOCKING?: boolean;
  children?: ReactElement;
};

const getIdentifier = (type: string, userId: string | undefined) =>
  type.toLowerCase() === 'userid' ? userId || getUserState().user.user_id : Cookie.getCookie(type);

const WithExperiment = ({
  BUCKETS,
  IDENTIFIER,
  NAME,
  NON_BUCKETED = undefined,
  BLOCKING = false,
  children
}: WithExperimentProps) => {
  const ENVIRONMENT = process.env.CARVANA_APP_ENVIRONMENT as Environment;
  const [isMounted, setIsMounted] = useState(false);
  const [identifier, setIdentifier] = useState<string | null>(null);

  const assignedBucket = useExperimentBucket(NAME);

  const experimentProps: AdditionalExperimentProps = {
    token: undefined,
    nonBucketedExperiment: NON_BUCKETED
  };

  const userState = getUserState();
  const userIdIdentifier = userState?.user?.user_id;

  useEffect(() => {
    setIsMounted(true);
    const identifierValue = getIdentifier(IDENTIFIER, userIdIdentifier);
    setIdentifier(identifierValue);
  }, [IDENTIFIER, userIdIdentifier]);

  useEffect(() => {
    if (!assignedBucket || !NAME) {
      return;
    }
    datadogRum.addFeatureFlagEvaluation(NAME, assignedBucket);
    window.FS?.setUserVars({ [`${NAME}_str`]: assignedBucket });
  }, [assignedBucket, NAME]);

  if (!identifier) {
    return <>{children}</>;
  }

  // Show spinner while waiting for bucket assignment (if BLOCKING is enabled)
  // We need both isMounted (to avoid hydration issues) AND assignedBucket
  const shouldBlock = BLOCKING && (!isMounted || !assignedBucket) && !NON_BUCKETED;

  return (
    <Experiment
      environment={ENVIRONMENT}
      identifier={identifier}
      loading={null}
      name={NAME}
      {...experimentProps}
    >
      {shouldBlock ? <LoadingWrapper /> : <When bucket={BUCKETS}>{children}</When>}
    </Experiment>
  );
};

export default WithExperiment;
